import { useEffect, useRef, useState, useCallback } from "react";

export const isSuccessStatus = (status) =>
    status === "Completed" || status === "Extracted" || status === "Done" || status === "Success";

export const isFailedStatus = (status) =>
    status?.toLowerCase().includes("fail") ||
    status?.toLowerCase().includes("error") ||
    status?.toLowerCase().includes("missing");

const WS_MAX_RECONNECT = 10;

// Connects to the tenant WebSocket group and tracks `resume_upload_updated`
// events keyed by resume_upload_id, so an upload panel can render live
// per-file progress without needing Stage4InviteCandidates mounted.
export default function useResumeUploadSocket() {
    const [uploadItems, setUploadItems] = useState({});

    const socketRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const intentionalCloseRef = useRef(false);

    useEffect(() => {
        const token = localStorage.getItem("token");
        const tenantId = localStorage.getItem("tenentId");
        const baseUrl = process.env.REACT_APP_WS_URL;
        if (!token || !tenantId || !baseUrl) return;

        intentionalCloseRef.current = false;

        const connect = () => {
            if (intentionalCloseRef.current) return;

            const wsUrl = `${baseUrl}?token=${encodeURIComponent(token)}`;
            const socket = new WebSocket(wsUrl);
            socketRef.current = socket;

            socket.onopen = () => {
                if (socketRef.current !== socket) return;
                reconnectAttemptsRef.current = 0;
                socket.send(JSON.stringify({ action: "join", group: `tenant-${tenantId}` }));
            };

            socket.onmessage = (event) => {
                try {
                    const parsed = JSON.parse(event.data);
                    if (parsed.event === "resume_upload_updated" && parsed.data?.id) {
                        const d = parsed.data;
                        setUploadItems((prev) => ({
                            ...prev,
                            [d.id]: {
                                file_name: d.file_name || prev[d.id]?.file_name,
                                status: d.status,
                                progress: d.progress,
                                ui_message: d.ui_message,
                                candidate_name: d.candidate_name || prev[d.id]?.candidate_name || null,
                                candidate_email: d.candidate_email || prev[d.id]?.candidate_email || null,
                                candidate_phone: d.candidate_phone || prev[d.id]?.candidate_phone || null,
                                candidate_id: d.candidate_id || prev[d.id]?.candidate_id || null,
                            },
                        }));
                    }
                } catch (err) {
                    console.error("Error parsing WebSocket message:", err);
                }
            };

            socket.onclose = () => {
                if (intentionalCloseRef.current) return;
                if (socketRef.current !== socket) return;
                if (reconnectAttemptsRef.current >= WS_MAX_RECONNECT) return;
                const delay = Math.min(30000, 1000 * Math.pow(2, reconnectAttemptsRef.current));
                reconnectAttemptsRef.current += 1;
                reconnectTimerRef.current = setTimeout(connect, delay);
            };

            socket.onerror = () => {
                if (socketRef.current === socket) socket.close();
            };
        };

        connect();

        return () => {
            intentionalCloseRef.current = true;
            clearTimeout(reconnectTimerRef.current);
            if (socketRef.current) socketRef.current.close();
        };
    }, []);

    // Seeds uploadItems immediately after an upload API call resolves, so the
    // UI shows the file list before the first WS progress event arrives.
    const registerItems = useCallback((items) => {
        setUploadItems((prev) => {
            const next = { ...prev };
            items.forEach((item) => {
                if (item.resume_upload_id) {
                    next[item.resume_upload_id] = {
                        file_name: item.filename || item.file_name || "Resume",
                        status: "Uploading",
                        progress: 0,
                        ui_message: `Uploading "${item.filename || "Resume"}"…`,
                        candidate_name: null,
                        candidate_email: null,
                        candidate_phone: null,
                        candidate_id: null,
                    };
                }
            });
            return next;
        });
    }, []);

    return { uploadItems, registerItems };
}
